import {useCallback, useState} from 'react';

/**
 * Manage the selection state of a table's rows.
 * @param numRows The number of rows.
 * @param disabledRowKeys A list of row indices corresponding to unselectable rows.
 * @returns The {@type Set} of selected row keys, and callbacks for toggling and resetting the selected rows.
 */
export default function useRowSelectionState(
  numRows: number,
  disabledRowIndices: Set<number> = new Set()
): {
  rowSelectionState: ReadonlyArray<boolean>;
  toggleRowSelected: (rowIndexToToggle: number) => void;
  resetAllSelectionState: () => void;
  toggleAllRowsSelected: () => void;
} {
  const [deselectedRows, setDeselectedRows] = useState<Set<number>>(disabledRowIndices);

  const toggleRowSelected = useCallback(
    (rowIndexToToggle: number) => {
      if (disabledRowIndices.has(rowIndexToToggle)) {
        return;
      }
      const newDeselectedRows = new Set([...deselectedRows]);
      if (deselectedRows.has(rowIndexToToggle)) {
        newDeselectedRows.delete(rowIndexToToggle);
      } else {
        newDeselectedRows.add(rowIndexToToggle);
      }
      setDeselectedRows(newDeselectedRows);
    },
    [deselectedRows, disabledRowIndices]
  );

  // Resets back to default state
  const resetAllSelectionState = useCallback(() => {
    setDeselectedRows(disabledRowIndices);
  }, [disabledRowIndices]);

  const toggleAllRowsSelected = useCallback(() => {
    if (deselectedRows.size > disabledRowIndices.size) {
      // If some non-disabled rows are unselected, select all rows except for disabled rows
      resetAllSelectionState();
    } else {
      // If all rows are selected, unselect all rows, including disabled
      setDeselectedRows(
        new Set(
          Array(numRows)
            .fill(null)
            .map((_, index) => index)
        )
      );
    }
  }, [deselectedRows.size, disabledRowIndices.size, resetAllSelectionState, numRows]);

  return {
    rowSelectionState: Array(numRows)
      .fill(false)
      .map((_, index) => !deselectedRows.has(index)),
    toggleRowSelected,
    resetAllSelectionState,
    toggleAllRowsSelected,
  };
}
